import React, {useState, useRef, useCallback} from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Stepper,
    Step,
    StepLabel,
    Grid,
    CircularProgress,
    Alert,
    Divider,
    useTheme,
    alpha,
    Dialog,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    DialogTitle,
    Backdrop,
    LinearProgress
} from '@mui/material';
import {
    Camera,
    Upload,
    Check,
    AlertCircle,
    HelpCircle,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    Trash2,
    Maximize2,
    X,
    Eye
} from 'react-feather';
import {ApiDetails, KEY} from "../../dummyData";
import CryptoJS from "crypto-js";

const IDUploadPage = () => {
    const theme = useTheme();
    const [activeStep, setActiveStep] = useState(0);
    const [idFront, setIdFront] = useState(null);
    const [idBack, setIdBack] = useState(null);
    const [previewFront, setPreviewFront] = useState(null);
    const [previewBack, setPreviewBack] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [previewDialogImage, setPreviewDialogImage] = useState(null);
    const [previewDialogTitle, setPreviewDialogTitle] = useState('');
    const fileInputRefFront = useRef(null);
    const fileInputRefBack = useRef(null);

    let details = CryptoJS.AES.decrypt(
        localStorage.getItem("tenant_details"),
        KEY
    );

    let originalText = details.toString(CryptoJS.enc.Utf8);

    // Steps for the upload process
    const steps = ['Upload Front Side', 'Upload Back Side', 'Review & Submit'];

    // Handle file selection for the front of ID
    const handleFrontIdSelect = (event) => {
        console.log("Handling front ID selection", event);
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                setUploadError("Please upload a valid image file (JPG, PNG)");
                return;
            }

            // Validate file size (10MB max)
            if (file.size > 10 * 1024 * 1024) {
                setUploadError("File size exceeds 10MB limit");
                return;
            }

            setIdFront(file);

            // Create a preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewFront(reader.result);
            };
            reader.onerror = () => {
                setUploadError("Failed to read the selected file. Please try again.");
            };
            reader.readAsDataURL(file);
            setUploadError(null);
        }
    };

    // Handle file selection for the back of ID
    const handleBackIdSelect = (event) => {
        console.log("Handling back ID selection", event);
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                setUploadError("Please upload a valid image file (JPG, PNG)");
                return;
            }

            // Validate file size (10MB max)
            if (file.size > 10 * 1024 * 1024) {
                setUploadError("File size exceeds 10MB limit");
                return;
            }

            setIdBack(file);

            // Create a preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewBack(reader.result);
            };
            reader.onerror = () => {
                setUploadError("Failed to read the selected file. Please try again.");
            };
            reader.readAsDataURL(file);
            setUploadError(null);
        }
    };

    // Remove front ID image
    const removeFrontId = () => {
        setIdFront(null);
        setPreviewFront(null);
        if (fileInputRefFront.current) {
            fileInputRefFront.current.value = '';
        }
    };

    // Remove back ID image
    const removeBackId = () => {
        setIdBack(null);
        setPreviewBack(null);
        if (fileInputRefBack.current) {
            fileInputRefBack.current.value = '';
        }
    };

    // Move to next step
    const handleNext = () => {
        if (activeStep === 0 && !idFront) {
            setUploadError("Please upload the front side of your National ID");
            return;
        }
        if (activeStep === 1 && !idBack) {
            setUploadError("Please upload the back side of your National ID");
            return;
        }

        setActiveStep((prevStep) => prevStep + 1);
        setUploadError(null);
    };

    // Move to previous step
    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
        setUploadError(null);
    };

    // Reset the whole process
    const handleReset = () => {
        setActiveStep(0);
        setIdFront(null);
        setIdBack(null);
        setPreviewFront(null);
        setPreviewBack(null);
        setUploadSuccess(false);
        setUploadError(null);
        if (fileInputRefFront.current) {
            fileInputRefFront.current.value = '';
        }
        if (fileInputRefBack.current) {
            fileInputRefBack.current.value = '';
        }
    };

    // Handle final submission
    const handleSubmit = async () => {
        if (!idFront || !idBack) {
            setUploadError("Both front and back ID images are required");
            return;
        }

        setUploadLoading(true);
        setUploadError(null);

        try {
            const tenantMap = new Map(Object.entries(JSON.parse(originalText)));

            const formData = new FormData();
            formData.append('id_front', idFront);
            formData.append('id_back', idBack);
            formData.append('tenant_id', tenantMap.get("tenant_id"));
            formData.append('landlord_id', tenantMap.get("landlord_id"));
            formData.append('property_id', tenantMap.get("property_id"));

            const response = await fetch(ApiDetails + "pegasus/visionary/tenant/app/tenant/ID/upload", {
                method: "POST",
                headers: {
                    "token": tenantMap.get("access_token"),
                },
                body: formData,
            });

            if (response.status === 403) {
                localStorage.clear();
                window.location.href = "/";
                return;
            }

            const data = await response.json();

            if (response.status === 200) {
                setUploadSuccess(true);
                setActiveStep(3);
            } else {
                setUploadError(data.Error || data.error || data.message || "Upload failed. Please try again.");
            }
        } catch (error) {
            console.error('Error uploading ID:', error);
            setUploadError("Failed to upload your ID. Please check your connection and try again.");
        } finally {
            setUploadLoading(false);
        }
    };

    // Trigger file input click
    const triggerFileSelectFront = () => {
        if (fileInputRefFront.current) {
            fileInputRefFront.current.click();
        }
    };

    const triggerFileSelectBack = () => {
        if (fileInputRefBack.current) {
            fileInputRefBack.current.click();
        }
    };

    // Open full-screen preview dialog
    const openPreviewDialog = (image, title) => {
        setPreviewDialogImage(image);
        setPreviewDialogTitle(title);
        setPreviewDialogOpen(true);
    };

    // Close preview dialog
    const closePreviewDialog = () => {
        setPreviewDialogOpen(false);
    };

    // Render upload card for ID side
    const renderUploadCard = (side, previewUrl, removeFile) => {
        const isUploaded = side === 'front' ? !!idFront : !!idBack;
        const sideLabel = side === 'front' ? 'Front' : 'Back';
        const fileInputRef = side === 'front' ? fileInputRefFront : fileInputRefBack;
        const handleUpload = side === 'front' ? handleFrontIdSelect : handleBackIdSelect;
        const triggerSelect = side === 'front' ? triggerFileSelectFront : triggerFileSelectBack;

        return (
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    border: '1px dashed',
                    borderColor: isUploaded ? alpha(theme.palette.success.main, 0.5) : alpha(theme.palette.primary.main, 0.5),
                    borderRadius: 2,
                    backgroundColor: isUploaded ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.primary.main, 0.05),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    height: 250,
                    width: '100%',
                    overflow: 'hidden'
                }}
            >
                {isUploaded ? (
                    <>
                        <Box
                            component="img"
                            src={previewUrl}
                            alt={`ID ${side} side`}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                borderRadius: 1
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                p: 1,
                                display: 'flex',
                                gap: 1
                            }}
                        >
                            <Tooltip title="View Full Screen">
                                <IconButton
                                    size="small"
                                    onClick={() => openPreviewDialog(previewUrl, `National ID - ${sideLabel} Side`)}
                                    sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)'
                                        }
                                    }}
                                >
                                    <Maximize2 size={16}/>
                                </IconButton>
                            </Tooltip>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={removeFile}
                                startIcon={<Trash2 size={16}/>}
                                sx={{borderRadius: 8, boxShadow: 2}}
                            >
                                Remove
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={triggerSelect}
                                startIcon={<RefreshCw size={16}/>}
                                sx={{borderRadius: 8, boxShadow: 2}}
                            >
                                Change
                            </Button>
                        </Box>
                        <Check
                            color={theme.palette.success.main}
                            size={28}
                            style={{
                                position: 'absolute',
                                bottom: 10,
                                right: 10,
                                background: 'white',
                                borderRadius: '50%',
                                padding: 4
                            }}
                        />
                    </>
                ) : (
                    <>
                        <input
                            type="file"
                            hidden
                            ref={fileInputRef}
                            onChange={handleUpload}
                            accept="image/*"
                        />
                        <Box
                            sx={{
                                height: '100%',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <Upload color={theme.palette.primary.main} size={48}/>
                            <Typography variant="h6" align="center">
                                Upload {sideLabel} Side
                            </Typography>
                            <Typography variant="body2" align="center" color="textSecondary" sx={{mb: 2}}>
                                Click to upload or drag and drop<br/>JPG, PNG (max 10MB)
                            </Typography>
                            <Box sx={{display: 'flex', gap: 2}}>
                                <Button
                                    variant="outlined"
                                    onClick={triggerSelect}
                                    startIcon={<Upload size={16}/>}
                                    sx={{borderRadius: 8}}
                                >
                                    Browse Files
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={triggerSelect}
                                    startIcon={<Camera size={16}/>}
                                    sx={{borderRadius: 8}}
                                >
                                    Take Photo
                                </Button>
                            </Box>
                        </Box>
                    </>
                )}
            </Paper>
        );
    };

    // Render a preview image card for review step
    const renderPreviewCard = (image, title, hasImage = true) => {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 1,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: hasImage ? alpha(theme.palette.success.main, 0.3) : 'divider',
                    height: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    position: 'relative'
                }}
            >
                <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{
                        px: 1,
                        pt: 1,
                        pb: 0.5,
                        borderBottom: hasImage ? '1px solid' : 'none',
                        borderColor: hasImage ? alpha(theme.palette.success.main, 0.3) : 'transparent',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    {title}
                    {hasImage && (
                        <IconButton
                            size="small"
                            onClick={() => openPreviewDialog(image, title)}
                            sx={{ml: 'auto'}}
                        >
                            <Maximize2 size={14}/>
                        </IconButton>
                    )}
                </Typography>

                <Box sx={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                }}>
                    {hasImage ? (
                        <Box
                            component="img"
                            src={image}
                            alt={title}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                            }}
                        />
                    ) : (
                        <Typography color="text.secondary">No image uploaded</Typography>
                    )}
                </Box>

                {hasImage && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        p: 0.5,
                        borderTop: '1px solid',
                        borderColor: alpha(theme.palette.success.main, 0.3)
                    }}>
                        <Button
                            size="small"
                            startIcon={<Eye size={14}/>}
                            onClick={() => openPreviewDialog(image, title)}
                        >
                            Preview
                        </Button>
                    </Box>
                )}
            </Paper>
        );
    };

    // Render the content for each step
    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Upload Front Side of National ID
                        </Typography>
                        <Typography variant="body2" paragraph color="textSecondary">
                            Please ensure that all information is clearly visible and the entire card is in frame.
                        </Typography>
                        {renderUploadCard('front', previewFront, removeFrontId)}

                        {/* Show preview of back side if already uploaded */}
                        {previewBack && (
                            <Box sx={{mt: 3}}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 1
                                }}>
                                    <Typography variant="subtitle2">Back Side (Already uploaded)</Typography>
                                    <Button
                                        variant="text"
                                        size="small"
                                        color="primary"
                                        onClick={() => setActiveStep(1)}
                                        endIcon={<ChevronRight size={16}/>}
                                    >
                                        Edit back side
                                    </Button>
                                </Box>
                                {renderPreviewCard(previewBack, 'ID Back Side Preview')}
                            </Box>
                        )}
                    </Box>
                );
            case 1:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Upload Back Side of National ID
                        </Typography>
                        <Typography variant="body2" paragraph color="textSecondary">
                            Please ensure that all information is clearly visible and the entire card is in frame.
                        </Typography>
                        {renderUploadCard('back', previewBack, removeBackId)}

                        {/* Show preview of front side if already uploaded */}
                        {previewFront && (
                            <Box sx={{mt: 3}}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 1
                                }}>
                                    <Typography variant="subtitle2">Front Side (Already uploaded)</Typography>
                                    <Button
                                        variant="text"
                                        size="small"
                                        color="primary"
                                        onClick={() => setActiveStep(0)}
                                        startIcon={<ChevronLeft size={16}/>}
                                    >
                                        Edit front side
                                    </Button>
                                </Box>
                                {renderPreviewCard(previewFront, 'ID Front Side Preview')}
                            </Box>
                        )}
                    </Box>
                );
            case 2:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Review & Submit
                        </Typography>
                        <Typography variant="body2" paragraph color="textSecondary">
                            Please review your National ID images before submitting. Click on any image to view it in
                            full screen.
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                {renderPreviewCard(previewFront, 'Front Side of National ID', !!previewFront)}
                                {!previewFront && (
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => setActiveStep(0)}
                                        sx={{mt: 1}}
                                    >
                                        Go back to upload front side
                                    </Button>
                                )}
                            </Grid>

                            <Grid item xs={12} md={6}>
                                {renderPreviewCard(previewBack, 'Back Side of National ID', !!previewBack)}
                                {!previewBack && (
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => setActiveStep(1)}
                                        sx={{mt: 1}}
                                    >
                                        Go back to upload back side
                                    </Button>
                                )}
                            </Grid>
                        </Grid>

                        <Box sx={{mt: 4, p: 2, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 2}}>
                            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
                                <HelpCircle color={theme.palette.info.main} size={20} style={{marginTop: 2}}/>
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Important Information
                                    </Typography>
                                    <Typography variant="body2">
                                        By submitting these documents, you confirm that the information provided is
                                        accurate and belongs to you.
                                        Your ID will be securely stored and used only for verification purposes in
                                        accordance with our privacy policy.
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{mt: 3, display: 'flex', justifyContent: 'center'}}>
                            <Alert
                                severity="info"
                                icon={<AlertCircle size={20}/>}
                                sx={{width: '100%'}}
                            >
                                Once you click Submit, both ID images will be securely sent to our verification system.
                            </Alert>
                        </Box>
                    </Box>
                );
            case 3:
                return (
                    <Box sx={{textAlign: 'center', py: 4}}>
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                backgroundColor: alpha(theme.palette.success.main, 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3
                            }}
                        >
                            <Check color={theme.palette.success.main} size={40}/>
                        </Box>
                        <Typography variant="h5" gutterBottom>
                            ID Successfully Uploaded
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Your National ID has been successfully uploaded and is pending verification.
                        </Typography>
                        <Typography variant="body2" color="textSecondary" paragraph>
                            We'll notify you once the verification process is complete. This typically takes 1-2
                            business days.
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleReset}
                            sx={{mt: 2, borderRadius: 8}}
                        >
                            Upload Different ID
                        </Button>
                    </Box>
                );
            default:
                return 'Unknown step';
        }
    };

    return (
        <Box sx={{maxWidth: 800, mx: 'auto', p: {xs: 2, md: 3}}}>
            <Paper elevation={0} sx={{p: {xs: 2, md: 4}, borderRadius: 3, border: '1px solid', borderColor: 'divider'}}>
                <Typography variant="h5" gutterBottom sx={{mb: 3}}>
                    National ID Verification
                </Typography>

                {activeStep < 3 && (
                    <Stepper activeStep={activeStep} alternativeLabel sx={{mb: 4}}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                )}

                {uploadError && (
                    <Alert
                        severity="error"
                        sx={{mb: 3}}
                        icon={<AlertCircle size={20}/>}
                        onClose={() => setUploadError(null)}
                    >
                        {uploadError}
                    </Alert>
                )}

                <Box>
                    {getStepContent(activeStep)}
                </Box>

                {activeStep < 3 && (
                    <>
                        <Divider sx={{my: 4}}/>

                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                            <Button
                                disabled={activeStep === 0}
                                onClick={handleBack}
                                variant="outlined"
                                startIcon={<ChevronLeft size={16}/>}
                                sx={{borderRadius: 8}}
                            >
                                Back
                            </Button>
                            <Box>
                                {activeStep === steps.length - 1 ? (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSubmit}
                                        disabled={uploadLoading || !idFront || !idBack}
                                        sx={{borderRadius: 8}}
                                    >
                                        {uploadLoading ? (
                                            <>
                                                <CircularProgress size={16} color="inherit" sx={{mr: 1}}/>
                                                Uploading...
                                            </>
                                        ) : (
                                            'Submit'
                                        )}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        endIcon={<ChevronRight size={16}/>}
                                        sx={{borderRadius: 8}}
                                    >
                                        Next
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </>
                )}
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    mt: 3,
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                }}
            >
                <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 2}}>
                    <HelpCircle color={theme.palette.primary.main} size={24} style={{marginTop: 2}}/>
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            ID Upload Guidelines
                        </Typography>
                        <Typography variant="body2" paragraph>
                            Follow these tips to ensure a successful verification:
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" component="div">
                                    <ul style={{paddingLeft: 16, marginTop: 0}}>
                                        <li>Make sure the entire ID is visible and in focus</li>
                                        <li>Ensure all text and information is clearly legible</li>
                                        <li>Upload both front and back sides of your ID</li>
                                    </ul>
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" component="div">
                                    <ul style={{paddingLeft: 16, marginTop: 0}}>
                                        <li>Avoid glare or shadows on the ID</li>
                                        <li>Use good lighting for best results</li>
                                        <li>File size should not exceed 10MB</li>
                                    </ul>
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Paper>

            {/* Full-screen preview dialog */}
            <Dialog
                open={previewDialogOpen}
                onClose={closePreviewDialog}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxHeight: '90vh',
                        height: 'auto'
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 3,
                    py: 2
                }}>
                    <Typography variant="h6">{previewDialogTitle}</Typography>
                    <IconButton onClick={closePreviewDialog} edge="end" aria-label="close">
                        <X size={20}/>
                    </IconButton>
                </DialogTitle>

                <Divider/>

                <DialogContent sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                    minHeight: '60vh'
                }}>
                    {previewDialogImage && (
                        <Box
                            component="img"
                            src={previewDialogImage}
                            alt="ID Preview"
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '70vh',
                                objectFit: 'contain',
                                borderRadius: 1
                            }}
                        />
                    )}
                </DialogContent>

                <DialogActions sx={{p: 2}}>
                    <Button
                        variant="outlined"
                        onClick={closePreviewDialog}
                        sx={{borderRadius: 8}}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Upload Loading Overlay */}
            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(4px)'
                }}
                open={uploadLoading}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 3,
                        p: 4,
                        borderRadius: 3,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        minWidth: 300
                    }}
                >
                    <CircularProgress
                        size={60}
                        thickness={4}
                        sx={{
                            color: 'white'
                        }}
                    />
                    <Box sx={{ width: '100%' }}>
                        <Typography variant="h6" align="center" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
                            Uploading Your ID...
                        </Typography>
                        <LinearProgress
                            sx={{
                                mt: 2,
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 3,
                                    backgroundColor: 'white'
                                }
                            }}
                        />
                        <Typography variant="body2" align="center" sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.8)' }}>
                            Please wait while we securely upload your documents...
                        </Typography>
                    </Box>
                </Box>
            </Backdrop>
        </Box>
    );
};

export default IDUploadPage;